import isLocalePresentInUrl from '~/utils/routing/isLocalePresentInUrl'
import sendLocaleChange from '~/utils/locale/sendLocaleChange'
import fetchLanguagesConfig from '~/utils/config/languages/fetchLanguagesConfig'
import type { Language } from '~/types/storeTypes'
import type { RouteLocationNormalized } from 'vue-router'
import getDefaultLocale from '~/utils/locale/getDefaultLocale'
import { H3Event } from 'h3'

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  if (to.name === 'status' || to.name === 'locale-status') {
    return
  }

  const localeSetting = useState<string>('locale.setting')
  const event = useRequestEvent()
  const defaultLanguageIso = getDefaultLocale().iso

  const isLocalePresent = isLocalePresentInUrl(to.path)
  const localeInUrl = isLocalePresent ? to.path.split('/')[1].toLowerCase() : defaultLanguageIso.toLowerCase()

  const languagesList = await getLanguagesList(event)

  const isValidLocale = languagesList.some((lang) => lang.iso_code.toLowerCase() === localeInUrl)

  let locale = localeSetting.value

  const isDefaultLocalePartOfLangList =
    languagesList.length && languagesList.some((lang) => lang.iso_code.toLowerCase() === defaultLanguageIso)

  if (!locale) {
    locale = isDefaultLocalePartOfLangList ? defaultLanguageIso : languagesList?.[0].iso_code.toLowerCase()
  }

  if (!isLocalePresent || (isLocalePresent && !isValidLocale)) {
    if (!isDefaultLocalePartOfLangList) {
      locale = languagesList?.[0].iso_code.toLowerCase()
    }

    const routeName = String(to.name)
    const localizedRouteName = routeName.startsWith('locale-') ? routeName : `locale-${routeName}`

    return navigateTo({
      name: localizedRouteName,
      params: {
        ...to.params,
        locale,
      },
      query: to.query,
    })
  }

  // If locale was manually edited in url, change selected locale.
  if (isLocalePresent && locale !== localeInUrl) {
    localeSetting.value = isValidLocale ? localeInUrl : defaultLanguageIso

    await sendLocaleChange(localeSetting.value)
  }
})

async function getLanguagesList(event: H3Event): Promise<Pick<Language, 'id' | 'iso_code'>[]> {
  const languagesList = useState<Pick<Language, 'id' | 'iso_code'>[]>('locale.languagesList')

  if (!languagesList.value) {
    languagesList.value = await fetchLanguagesConfig(event)
  }

  return languagesList.value
}
